import { Line } from "vue-chartjs";
import { ChartConfig } from "@/config/chart.js";
import Chart from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "chartjs-adapter-moment";
import { PERIODIC_REPORT_TYPE } from "@/enums/charts-type.enum";

export default {
  extends: Line,
  props: {
    labels: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    },
    maxValueY: {
      type: Number,
      required: true
    },
    selectedReportType: {
      type: Number,
      required: true,
      validator: val => Object.values(PERIODIC_REPORT_TYPE).includes(val)
    }
  },
  computed: {
    isTopReport() {
      return this.selectedReportType === PERIODIC_REPORT_TYPE.TOP;
    },
    stepSize() {
      switch (true) {
        case this.maxValueY < 10:
          return 2;
        case this.maxValueY < 25:
          return 5;
        case this.maxValueY < 100:
          return 10;
        case this.maxValueY < 300:
          return 25;
        case this.maxValueY < 500:
          return 50;
        case this.maxValueY < 750:
          return 100;
        case this.maxValueY < 1500:
          return 250;
        default:
          return 500;
      }
    },
    chartOptions() {
      return {
        tooltips: {
          callbacks: {
            label: this.isTopReport
              ? function(tooltipItem, data) {
                  return `${data.datasets[tooltipItem.datasetIndex].label}: ${
                    tooltipItem.yLabel
                  }`;
                }
              : function(tooltipItem, data) {
                  if (!this.isTopReport) {
                    tooltipItem.yLabel > 20
                      ? (tooltipItem.yLabel = "> 20")
                      : tooltipItem.yLabel;
                  }

                  return `${data.datasets[tooltipItem.datasetIndex].label}: ${
                    tooltipItem.yLabel
                  }`;
                }
          }
        },
        layout: {
          padding: 30
        },
        responsive: true,
        maintainAspectRatio: false,
        legend: false,
        scales: {
          xAxes: [
            {
              ticks: {
                display: true,
                fontSize: ChartConfig.fontSize,
                padding: ChartConfig.padding,
                fontFamily: ChartConfig.fontFamily,
                fontColor: ChartConfig.fontColor
              },
              type: "time",
              time: {
                minUnit: "day",
                displayFormats: {
                  minute: "DD MMM YY",
                  hour: "DD MMM YY",
                  day: "DD MMM YY",
                  week: "DD MMM YY",
                  month: "DD MMM YY"
                }
              },
              display: true,
              gridLines: {
                drawOnChartArea: false
              }
            }
          ],
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: this.$t(
                  `message.analytics.periodic.chart.yAxisLabel.${this.selectedReportType}`
                ),
                fontSize: this.isTopReport ? 16 : 14,
                padding: {
                  top: 10,
                  bottom: 10
                }
              },
              ticks: {
                fontSize: ChartConfig.fontSize,
                padding: ChartConfig.padding,
                fontFamily: ChartConfig.fontFamily,
                fontColor: ChartConfig.fontColor,
                suggestedMin: this.isTopReport ? 0 : undefined,
                min: this.isTopReport ? 0 : 1,
                max: this.isTopReport ? undefined : 21,
                beginAtZero: this.isTopReport,
                reverse: !this.isTopReport,
                callback: this.isTopReport
                  ? function(value) {
                      if (value === this.maxValueY) {
                        return value;
                      }
                      return value;
                    }
                  : function(value, index, values) {
                      values.some(val =>
                        val > 20 ? (values[values.length - 1] = 21) : false
                      );

                      if (value > 20) {
                        return "> 20";
                      }
                      return value;
                    }
              }
            }
          ]
        }
      };
    }
  },
  beforeCreate() {
    Chart.plugins.unregister(ChartDataLabels);
  },
  mounted() {
    this.$emit("loadingChartStatus", true);
    this.render();
    this.$emit("loadingChartStatus", false);
  },
  watch: {
    labels: async function() {
      this.render();
      this.$emit("loadingChartStatus", false);
    },
    data: async function() {
      this.render();
      this.$emit("loadingChartStatus", false);
    }
  },
  methods: {
    formatData(data) {
      return data.map(value => (value > 20 ? (value = 21) : value));
    },

    createDataSet(data) {
      const dataset = data
        .filter(item => item.visible)
        .map(item => {
          return {
            label: item.name,
            borderColor: item.color,
            borderWidth: 3,
            clip: 3,
            pointBorderColor: item.color,
            pointBackgroundColor: item.color,
            pointHoverBackgroundColor: item.color,
            pointHoverBorderColor: item.color,
            pointBorderWidth: 3,
            pointHoverRadius: 3,
            pointHoverBorderWidth: 3,
            pointRadius: 3,
            fill: false,
            backgroundColor: "transparent",
            data: this.isTopReport ? item.data : this.formatData(item.data)
          };
        });

      return dataset;
    },

    render() {
      this.renderChart(
        {
          labels: this.labels,
          datasets: this.createDataSet(this.data)
        },
        this.chartOptions
      );
    }
  }
};
